import React, { useEffect, useMemo, useState } from "react"
import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TableSortLabel from "@mui/material/TableSortLabel"
import Chip from "@mui/material/Chip"
import IconButton from "@mui/material/IconButton"
import Select from "@mui/material/Select"
import MenuItem from "@mui/material/MenuItem"
import { Form, Pagination } from "react-bootstrap"
import { ChevronRight, Eye, Pencil, RotateCw } from "lucide-react"
import RightModel from "./RightModel"
import { getIngestionStatus, getRagConfig, getRagDetails, proxyDatabricks, updateAllRagConfig, updateRagAppStatus, updateSingleRagStatus } from "../../service/ragApiServices"
import { useAzureData } from "../../context/AzureContext"
import { useNavigate } from "react-router-dom"

type Status = "Completed" | "Inprogress" | string

interface RagAppVersion {
    version: string
    rag_app_name: string
    rag_app_status: Status
    is_active: any
    created_by?: string
    created_date_time: string
    data_last_refreshed_date_time: string
    servicename: string
}

interface RagAppGroup {
    rag_app_name: string
    versions: RagAppVersion[]
}

interface Header {
    key: string
    label: string
    sortable?: boolean
}

interface RagAppsTableProps {
    searchTerm: string
    headers: Header[]
    initialGroups: RagAppGroup[]
    setRagData: any
    fetchData: any
}

function StatusChip({ status }: { status: string }) {
    const cls = status === "Completed" ? "status-chip completed" : status === "Inprogress" ? "status-chip inprogress" : "status-chip failed"
    return <Chip label={status} variant="outlined" className={cls} />
}

const versionRank = (v: string) => {
    const n = Number((v || "").toUpperCase().replace(/[^0-9]/g, ""))
    return isNaN(n) ? 0 : n
}

const sortVersionsDesc = (arr: RagAppVersion[]) => [...arr].sort((a, b) => versionRank(b.version) - versionRank(a.version));

export default function RagAppsTable({ searchTerm, headers, initialGroups, setRagData, fetchData }: RagAppsTableProps) {
    const { apiToken } = useAzureData();
    const navigate = useNavigate();
    const [groups, setGroups] = useState<RagAppGroup[]>(
        initialGroups
    )
    const [open, setOpen] = useState<Record<string, boolean>>({})
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(5)
    const [show, setShow] = useState(false);
    // eslint-disable-next-line
    const [showPopup, setShowPopup] = useState(false);
    const [ragDetails, setRagDetails] = useState(null);
    const [modelDataLoading, setModelDataLoading] = useState(false);
    const [ingestionStatus, setIngestionStatus] = useState([]);

    // Sorting state
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null)

    useEffect(() => {
        // setGroups(initialGroups)
        setGroups(initialGroups?.map((g) => ({ ...g, versions: sortVersionsDesc(g.versions) })))
        //eslint-disable-next-line
    }, [initialGroups])

    const requestSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev && prev.key === key) {
                return { key, direction: prev.direction === "asc" ? "desc" : "asc" }
            }
            return { key, direction: "asc" }
        })
    }

    const fetchRagDetails = async (ragAppName: any, createdDateTime: any) => {
        try {
            setModelDataLoading(true);
            const [ragConfig, ingestionStatus] = await Promise.all([await getRagConfig(apiToken, ragAppName, createdDateTime), await getIngestionStatus(apiToken, ragAppName)])

            // @ts-ignore
            if (ragConfig.data.status === 200) {
                // @ts-ignore
                setRagDetails(ragConfig.data.data[0]);
            } else {
                // @ts-ignore
                console.error("Error fetching RAG details:", ragConfig.data.message);
                setRagDetails(null)
            }
            // @ts-ignore
            if (ingestionStatus.data.status === 200) {
                // @ts-ignore
                setIngestionStatus(ingestionStatus.data.data);
            } else {
                setIngestionStatus([]);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setModelDataLoading(false);
        }
    };


    const filteredGroups = useMemo(() => {
        const s = (searchTerm || "").toLowerCase();
        if (!s && !sortConfig) return groups;

        const safeStr = (value: any) => (value ? String(value).toLowerCase() : "");

        let result = groups.filter((g) => {
            const groupName = safeStr(g.rag_app_name);
            return (
                groupName.includes(s) ||
                g.versions.some((v) =>
                    [
                        v.version,
                        v.rag_app_name,
                        v.rag_app_status,
                        v.created_date_time,
                        v.data_last_refreshed_date_time,
                    ].some((field) => safeStr(field).includes(s))
                )
            );
        });

        const { key, direction } = sortConfig || {};
        const dir = direction === "asc" ? 1 : -1;

        result = [...result].sort((a, b) => {
            // Sort by group-level appName
            if (key === "appName") {
                // @ts-ignore
                return dir * safeStr(a.appName).localeCompare(safeStr(b.appName));
            }

            // Sort by rag_app_name (first version of group)
            if (key === "rag_app_name") {
                return dir * safeStr(a.versions[0]?.rag_app_name).localeCompare(
                    safeStr(b.versions[0]?.rag_app_name)
                );
            }

            // Sort by date fields (created_date_time or data_last_refreshed_date_time)
            if (key === "created_date_time" || key === "data_last_refreshed_date_time") {
                const getDateTimestamp = (versions: typeof a.versions, k: string) => {
                    const timestamps = versions.map((v) => {
                        const t = new Date(v[k as keyof RagAppVersion]).getTime();
                        return isNaN(t) ? -Infinity : t; // Treat invalid dates as very old
                    });
                    return Math.min(...timestamps); // Earliest date among versions
                };

                return dir * (getDateTimestamp(a.versions, key) - getDateTimestamp(b.versions, key));
            }

            // Fallback: compare first version's string property
            const aValue = safeStr(a.versions[0]?.[key as keyof RagAppVersion]);
            const bValue = safeStr(b.versions[0]?.[key as keyof RagAppVersion]);
            return dir * aValue.localeCompare(bValue);
        });

        return result;
    }, [groups, searchTerm, sortConfig]);


    const total = filteredGroups?.length
    const pageCount = Math.max(1, Math.ceil(total / pageSize))
    const current = Math.min(page, pageCount)
    const start = (current - 1) * pageSize
    const visibleGroups = filteredGroups && filteredGroups.slice(start, start + pageSize)

    const toggleStatus = async (ragAppName: string, createdDateTime: string, currentStatus: number) => {
        try {
            // Find the RAG app group
            const appGroup = initialGroups.find((rag: any) => rag.rag_app_name === ragAppName);
            if (!appGroup) return;

            // Step 1: Check if disabling the selected version would leave no enabled versions
            if (currentStatus === 1) {
                const otherActiveVersions = appGroup.versions.filter(
                    (v: any) => v.is_active === 1 && v.created_date_time !== createdDateTime
                );

                if (otherActiveVersions.length === 0) {
                    alert("At least one version must remain enabled for this RAG app.");
                    return; // Prevent the toggle action
                }
            }

            // Step 2: If enabling, disable all other versions for the same RAG app
            if (currentStatus === 0) {
                const disableAllResponse = await updateAllRagConfig(apiToken, ragAppName);

                // @ts-ignore
                if (disableAllResponse.status !== 200) {
                    // @ts-ignore
                    console.error("Failed to disable other versions:", disableAllResponse.data.message);
                    return;
                }
            }

            // Step 3: Toggle the selected version's status
            const toggleResponse = await updateSingleRagStatus(apiToken, ragAppName, createdDateTime);

            // @ts-ignore
            if (toggleResponse.status === 200) {
                // Step 4: Update the local state
                setRagData((prevData: any) =>
                    prevData.map((rag: any) => {
                        if (rag.rag_app_name === ragAppName) {
                            return {
                                ...rag,
                                versions: rag.versions.map((v: any) => {
                                    if (v.created_date_time === createdDateTime) {
                                        // Toggle the clicked version
                                        return { ...v, is_active: currentStatus === 1 ? 0 : 1 };
                                    }
                                    // Disable other versions if enabling
                                    if (currentStatus === 0) {
                                        return { ...v, is_active: 0 };
                                    }
                                    return v;
                                }),
                            };
                        }
                        return rag;
                    })
                );

            } else {
                // @ts-ignore
                console.error("Error toggling status:", toggleResponse.data.message);
            }
        } catch (error) {
            console.error("Error processing toggle request:", error);
        }
    };

    const handleDataRefresh = async (ragAppName: any, createdDateTime: any) => {
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 1000);
        try {
            // Fetch RAG details
            const getResponse = await getRagDetails(apiToken, ragAppName, createdDateTime)

            // @ts-ignore
            const { data = [], status, message } = getResponse?.data

            if (status === 200) {
                const {
                    rag_app_name,
                    data_source_type,
                    entity,
                    servicename,
                    source_url,
                    source_url_sharepoint,
                    source_url_adls,
                    source_url_website_url
                } = data[0]

                let entityDetails = entity || {}

                if (entity) {
                    entityDetails = JSON.parse(entity);
                }

                const multipleUrls:any = [];
                const keys = Object.keys(entityDetails)
                keys.forEach((key) => {
                    const dataSet = entityDetails[key]
                    if (dataSet && dataSet.values.length) {
                        dataSet.values.forEach((value: any) => {
                            multipleUrls.push({ dataSourceType: dataSet.sourceType, entity: value, source_url: key })
                        })
                    }
                })

                // Check if the secret request is successful before proceeding
                const payload = {
                    data_source_type: data_source_type,
                    rag_app_name: rag_app_name,
                    rag_request_type: "Increment",
                    servicename: servicename,
                    source_url_sharepoint: source_url_sharepoint,
                    source_url_adls: source_url_adls,
                    source_url_website_url: source_url_website_url,
                    multipleurls: multipleUrls,
                    source_url: source_url,
                    is_old_rag_app: entity ? false : true
                };
                const postResponse = await proxyDatabricks(apiToken, payload)
                // @ts-ignore
                if (postResponse.status === 200) {
                    // Update RAG app status
                    const ragStatusResponse = await updateRagAppStatus(apiToken, ragAppName, createdDateTime);

                    // @ts-ignore
                    if (ragStatusResponse.status === 200) {
                        fetchData();
                    } else {
                        throw new Error("Failed to update the status.");
                    }

                } else {
                    // @ts-ignore
                    console.error("Error Databricks workflow:", postResponse.data.message || "Databricks workflow");
                }
            } else {
                console.error("Error fetching RAG details:", message);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    return (
        <>
            <TableContainer component={Paper} className="shadow-sm ragTable_container">
                <Table size="small" aria-label="RAG Apps table">
                    <TableHead>
                        <TableRow className="table_row">
                            {headers?.length > 0 && headers?.map((header) => (
                                <TableCell
                                    key={header.key}
                                    className="muted-head"
                                    sortDirection={sortConfig?.key === header.key ? sortConfig.direction : false}
                                >
                                    {header.sortable ? (
                                        <TableSortLabel
                                            active={sortConfig?.key === header.key}
                                            direction={sortConfig?.key === header.key ? sortConfig.direction : "asc"}
                                            onClick={() => requestSort(header.key)}
                                        >
                                            {header.label}
                                        </TableSortLabel>
                                    ) : (
                                        header.label
                                    )}
                                </TableCell>
                            ))}
                            <TableCell className="muted-head" align="center">
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleGroups?.map((g) => {
                            const versions = sortVersionsDesc(g.versions)
                            const parent = versions[0]
                            const children = versions.slice(1)
                            const isOpen = !!open[g.rag_app_name]

                            return (
                                <React.Fragment key={g.rag_app_name}>
                                    <TableRow hover className={`row-main ${isOpen ? "open" : ""}`}>
                                        <TableCell>
                                            <IconButton
                                                aria-label={isOpen ? "Collapse" : "Expand"}
                                                size="small"
                                                onClick={() => setOpen((prev) => ({ ...prev, [g.rag_app_name]: !prev[g.rag_app_name] }))}
                                                className={`arrow-btn ${isOpen ? "open" : ""}`}
                                            >
                                                <ChevronRight />
                                            </IconButton>
                                            <span className=" align-middle">{parent.version}</span>
                                        </TableCell>
                                        <TableCell>{parent.rag_app_name}</TableCell>
                                        <TableCell>
                                            <StatusChip status={parent.rag_app_status} />
                                        </TableCell>
                                        <TableCell>
                                            <Form.Check
                                                type="switch"
                                                checked={parent.is_active === 1}
                                                onChange={() =>
                                                    toggleStatus(
                                                        parent.rag_app_name,
                                                        parent.created_date_time,
                                                        parent.is_active
                                                    )
                                                }
                                                id={`switch-${g.rag_app_name}-${parent.version}-${parent.rag_app_name}`}
                                                label=""
                                            />
                                        </TableCell>
                                        <TableCell>{new Date(parent.created_date_time).toLocaleString()}</TableCell>
                                        <TableCell>{parent.data_last_refreshed_date_time ? new Date(parent.data_last_refreshed_date_time).toLocaleDateString() : "-"}</TableCell>
                                        <TableCell align="center">
                                            <IconButton aria-label="view" size="small">
                                                <Eye size={20} strokeWidth={2} color="#111111"
                                                    onClick={() => {
                                                        fetchRagDetails(parent.rag_app_name, parent.created_date_time)
                                                        setShow(true)
                                                    }}
                                                />
                                            </IconButton>
                                            <IconButton aria-label="edit" size="small">
                                                {/* @ts-ignore */}
                                                <Pencil onClick={() => parent.is_active === 0 ? "" : navigate(`/newragform?a=${parent?.rag_app_name}&t=${parent?.created_date_time}`)} size={18} strokeWidth={2} color={parent.is_active === 0 ? "#ccc" : "#111111"} style={{ cursor: parent.is_active === 0 && "not-allowed" }} />
                                            </IconButton>
                                                {/* @ts-ignore */}
                                            <IconButton aria-label="refresh" size="small">
                                                {/* @ts-ignore */}
                                                <RotateCw onClick={() => (parent.is_active === 0 || parent.rag_app_status === 'Inprogress') ? "" : handleDataRefresh(parent.rag_app_name, parent.created_date_time)} size={18} strokeWidth={2} color={(parent.is_active === 0 || parent.rag_app_status === 'Inprogress') ? "#ccc" : "#111111"} style={{ cursor: (parent.is_active === 0 || parent.rag_app_status === 'Inprogress') && "not-allowed" }} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>

                                    {isOpen &&
                                        children.map((v) => {
                                            const key = `${g.rag_app_name}-${v.version}-${v.rag_app_name}`
                                            return (
                                                <TableRow key={key} className="child-row">
                                                    <TableCell className="first-cell">
                                                        <span className="arrow-spacer" />
                                                        <span className="fw-semibold align-middle">{v.version}</span>
                                                    </TableCell>
                                                    <TableCell>{v.rag_app_name}</TableCell>
                                                    <TableCell>
                                                        <StatusChip status={v.rag_app_status} />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Form.Check
                                                            type="switch"
                                                            checked={v.is_active == 1}
                                                            onChange={() =>
                                                                toggleStatus(
                                                                    v.rag_app_name,
                                                                    v.created_date_time,
                                                                    v.is_active
                                                                )
                                                            }
                                                            id={`switch-${g.rag_app_name}-${v.version}-${v.rag_app_name}`}
                                                            label=""
                                                        />
                                                    </TableCell>
                                                    <TableCell>{new Date(v.created_date_time).toLocaleString()}</TableCell>
                                                    <TableCell>{v.data_last_refreshed_date_time ? new Date(v.data_last_refreshed_date_time).toLocaleDateString() : "-"}</TableCell>
                                                    <TableCell align="center">
                                                        <IconButton aria-label="view" size="small">
                                                            <Eye onClick={() => setShow(true)} size={20} strokeWidth={2} color="#111111" />
                                                        </IconButton>
                                                        <IconButton aria-label="edit" size="small">
                                                            {/* @ts-ignore */}
                                                            <Pencil onClick={() => parent.is_active === 0 ? "" : navigate(`/newragform?a=${v?.rag_app_name}&t=${v?.created_date_time}`)} size={18} strokeWidth={2} color={v.is_active === 0 ? "#ccc" : "#111111"} style={{ cursor: v.is_active === 0 && "not-allowed" }} />
                                                        </IconButton>
                                                        <IconButton aria-label="refresh" size="small">
                                                            {/* @ts-ignore */}
                                                            <RotateCw onClick={() => (v.is_active === 0 || v.rag_app_status === 'Inprogress') ? "" : handleDataRefresh(v.rag_app_name, v.created_date_time)} size={18} strokeWidth={2} color={(v.is_active === 0 || v.rag_app_status === 'Inprogress') ? "#ccc" : "#111111"} style={{ cursor: (v.is_active === 0 || v.rag_app_status === 'Inprogress') && "not-allowed" }} />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })}
                                </React.Fragment>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <div className="table-footer mt-3">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <div className="text-muted">Total: {total}</div>
                    <div className="d-flex align-items-center gap-3">
                        <Pagination className="mb-0">
                            <Pagination.Prev disabled={current === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />
                            <Pagination.Item active>{current}</Pagination.Item>
                            <Pagination.Next
                                disabled={current === pageCount}
                                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                            />
                        </Pagination>
                        <div className="text-muted small">of {pageCount}</div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <span className="text-muted">Show per page:</span>
                        <Select
                            size="small"
                            value={pageSize}
                            onChange={(e) => {
                                const next = Number(e.target.value)
                                setPageSize(next)
                                setPage(1)
                            }}
                            displayEmpty
                            className="page-size-select"
                        >
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                        </Select>
                    </div>
                </div>
            </div>
            <RightModel
                show={show}
                onClose={() => {
                    setShow(false)
                    setRagDetails(null)
                    setIngestionStatus([])
                }}
                ragData={ragDetails}
                ingestionStatusData={ingestionStatus}
                loading={modelDataLoading}
            />
        </>
    )
}
